/**
 * Form Options Constants for Community Onboarding
 * Extracted from CommunityOnboardingPage.tsx for better maintainability
 */

export const GOVERNANCE_OPTIONS = [
  'HOA Board Only',
  'HOA Board + Management Company',
  'Self-Managed Community',
  'Property Management Led',
  'Mixed Governance Structure',
  'Other/Custom Structure',
] as const;

// Governance options with descriptions for tooltips
export const GOVERNANCE_OPTIONS_WITH_DESCRIPTIONS = [
  {
    value: 'HOA Board Only',
    description: 'Community governed entirely by elected homeowner board members',
  },
  {
    value: 'HOA Board + Management Company',
    description: 'Board makes policy decisions, management company handles daily operations',
  },
  {
    value: 'Self-Managed Community',
    description: 'Residents handle all management tasks without professional assistance',
  },
  {
    value: 'Property Management Led',
    description: 'Professional management company handles most decisions and operations',
  },
  {
    value: 'Mixed Governance Structure',
    description: 'Combination of different governance approaches for different areas',
  },
  {
    value: 'Other/Custom Structure',
    description: 'Unique governance arrangement not covered by standard models',
  },
] as const;

export const CURRENT_SOFTWARE_OPTIONS = [
  'Excel/Google Sheets',
  'QuickBooks',
  'Basic HOA Software',
  'Multiple Point Solutions',
  'Custom Database',
  'Paper-Based Systems',
  'Email Only',
  'No Formal System',
  'Other',
] as const;

// Current software options with descriptions
export const CURRENT_SOFTWARE_OPTIONS_WITH_DESCRIPTIONS = [
  {
    value: 'Excel/Google Sheets',
    description: 'Spreadsheet-based tracking for finances, residents, maintenance',
  },
  {
    value: 'QuickBooks',
    description: 'Accounting software for financial management and bookkeeping',
  },
  {
    value: 'Basic HOA Software',
    description: 'Entry-level community management platform with essential features',
  },
  {
    value: 'Multiple Point Solutions',
    description:
      'Several different tools for different functions (accounting, communication, etc.)',
  },
  {
    value: 'Custom Database',
    description: 'Proprietary or custom-built system designed for your specific needs',
  },
  {
    value: 'Paper-Based Systems',
    description: 'Physical documents, filing systems, and manual processes',
  },
  {
    value: 'Email Only',
    description: 'Primary communication and coordination through email',
  },
  {
    value: 'No Formal System',
    description: 'Ad-hoc management without structured tools or processes',
  },
  {
    value: 'Other',
    description: 'Custom solution or software not listed above',
  },
] as const;

export const CHALLENGE_OPTIONS = [
  'Communication Inefficiencies',
  'Financial Management Complexity',
  'Vendor Coordination Issues',
  'Document Management Problems',
  'Resident Engagement Low',
  'Governance Transparency Lacking',
  'Maintenance Request Chaos',
  'Meeting Management Difficulties',
  'Compliance Tracking Issues',
  'Data Silos & Integration Problems',
] as const;

export const PAIN_POINT_OPTIONS = [
  'Manual bookkeeping takes too long',
  'Difficulty tracking payments',
  'Poor communication with residents',
  'Maintenance requests are chaotic',
  'Board meeting management',
  'Document storage and access',
  'Vendor coordination issues',
  'Compliance and legal concerns',
  'Lack of transparency',
  'Administrative overhead',
] as const;

export const CURRENT_FEATURE_OPTIONS = [
  'Online Payments',
  'Document Storage',
  'Communication Tools',
  'Meeting Management',
  'Vendor Management',
  'Maintenance Requests',
  'Financial Reporting',
  'Resident Directory',
  'Event Planning',
  'Voting Systems',
  'Violation Tracking',
  'Reserve Fund Management',
  'Insurance Management',
  'Amenity Reservations',
  'Property Inspections',
  'Accounting Integration',
  'Board Portal Access',
  'Resident Portal Access',
  'Vendor Portal Access',
  'Mobile App Access',
] as const;

// Current feature options with descriptions for tooltips
export const CURRENT_FEATURE_OPTIONS_WITH_DESCRIPTIONS = [
  {
    value: 'Online Payments',
    description:
      'Digital payment processing for HOA dues, assessments, and fees with automated tracking',
  },
  {
    value: 'Document Storage',
    description:
      'Secure cloud storage for bylaws, meeting minutes, financial records, and resident documents',
  },
  {
    value: 'Communication Tools',
    description: 'Mass email, announcements, newsletters, and resident messaging systems',
  },
  {
    value: 'Meeting Management',
    description:
      'Board meeting scheduling, agenda creation, minute taking, and attendance tracking',
  },
  {
    value: 'Vendor Management',
    description:
      'Contractor database, service scheduling, invoice processing, and performance tracking',
  },
  {
    value: 'Maintenance Requests',
    description: 'Resident work order submission, tracking, assignment, and completion workflow',
  },
  {
    value: 'Financial Reporting',
    description: 'Budget reports, income statements, balance sheets, and financial analytics',
  },
  {
    value: 'Resident Directory',
    description: 'Contact information, property details, emergency contacts, and resident profiles',
  },
  {
    value: 'Event Planning',
    description: 'Community event scheduling, RSVP management, and amenity booking coordination',
  },
  {
    value: 'Voting Systems',
    description: 'Digital voting for board elections, policy changes, and community decisions',
  },
  {
    value: 'Violation Tracking',
    description: 'Log, track, and manage homeowner violations, fines, and compliance issues',
  },
  {
    value: 'Reserve Fund Management',
    description:
      'Track reserve funds, plan major expenditures, and manage long-term financial planning',
  },
  {
    value: 'Insurance Management',
    description: 'Track property insurance policies, claims, certificates, and renewal schedules',
  },
  {
    value: 'Amenity Reservations',
    description:
      'Online booking system for clubhouse, pool, tennis courts, and other community amenities',
  },
  {
    value: 'Property Inspections',
    description:
      'Schedule and track routine property inspections, safety checks, and compliance audits',
  },
  {
    value: 'Accounting Integration',
    description: 'Direct integration with QuickBooks, Yardi, or other accounting software systems',
  },
  {
    value: 'Board Portal Access',
    description:
      'Secure board member portal for confidential documents, discussions, and decision making',
  },
  {
    value: 'Resident Portal Access',
    description:
      'Self-service portal for residents to pay dues, submit requests, access documents, and communicate',
  },
  {
    value: 'Vendor Portal Access',
    description:
      'Dedicated portal for contractors and vendors to receive work orders, submit invoices, and track payments',
  },
  {
    value: 'Mobile App Access',
    description:
      'Dedicated mobile application for residents and board members to access services on-the-go',
  },
] as const;

export const WISHLIST_FEATURE_OPTIONS = [
  'Integrated Financial Management',
  'Advanced Communication Hub',
  'Automated Vendor Coordination',
  'Smart Maintenance Scheduling',
  'Community Marketplace',
  'Digital Governance Tools',
  'Resident Engagement Platform',
  'Analytics & Insights',
  'Mobile-First Experience',
  'Multi-Community Management',
  'Municipal Integration',
  'Global Network Access',
] as const;

// Wishlist features with descriptions for tooltips
export const WISHLIST_FEATURE_OPTIONS_WITH_DESCRIPTIONS = [
  {
    value: 'Integrated Financial Management',
    description:
      'Complete financial suite: budgeting, A/R, A/P, reporting, reserve studies in one system',
    tooltip: 'integrated-financial-management',
  },
  {
    value: 'Advanced Communication Hub',
    description: 'Centralized communications: announcements, forums, messaging, emergency alerts',
    tooltip: 'advanced-communication-hub',
  },
  {
    value: 'Automated Vendor Coordination',
    description: 'Streamlined vendor management with automated scheduling and coordination',
  },
  {
    value: 'Smart Maintenance Scheduling',
    description: 'AI-powered maintenance optimization and predictive equipment management',
    tooltip: 'smart-maintenance-scheduling',
  },
  {
    value: 'Community Marketplace',
    description: 'Internal marketplace for resident-to-resident transactions and group buying',
    tooltip: 'community-marketplace',
  },
  {
    value: 'Digital Governance Tools',
    description: 'Electronic voting, digital meetings, automated compliance tracking',
    tooltip: 'digital-governance-tools',
  },
  {
    value: 'Resident Engagement Platform',
    description: 'Tools to increase participation in community activities and decisions',
  },
  {
    value: 'Analytics & Insights',
    description: 'Data dashboards for trends, spending patterns, and performance metrics',
    tooltip: 'analytics-insights',
  },
  {
    value: 'Mobile-First Experience',
    description: 'Fully-featured mobile apps for residents and management on-the-go',
  },
  {
    value: 'Multi-Community Management',
    description: 'Manage multiple properties/communities from one centralized dashboard',
    tooltip: 'multi-community-management',
  },
  {
    value: 'Municipal Integration',
    description: 'Direct connections with city/county systems for permits and compliance',
    tooltip: 'municipal-integration',
  },
  {
    value: 'Global Network Access',
    description: 'Connect with communities worldwide for best practices and group purchasing',
    tooltip: 'global-network-access',
  },
] as const;

export const GOAL_OPTIONS = [
  'Reduce Administrative Overhead',
  'Improve Communication',
  'Enhance Financial Transparency',
  'Streamline Vendor Management',
  'Increase Resident Satisfaction',
  'Modernize Operations',
  'Ensure Regulatory Compliance',
  'Drive Cost Savings',
  'Enable Growth/Scaling',
  'Create Competitive Advantage',
] as const;

// Goal options with descriptions for tooltips
export const GOAL_OPTIONS_WITH_DESCRIPTIONS = [
  {
    value: 'Reduce Administrative Overhead',
    description: 'Minimize time spent on paperwork, manual processes, and repetitive tasks',
  },
  {
    value: 'Improve Communication',
    description: 'Better information flow between board, management, and residents',
  },
  {
    value: 'Enhance Financial Transparency',
    description: 'Clear, accessible financial reporting and budget visibility for all stakeholders',
  },
  {
    value: 'Streamline Vendor Management',
    description: 'More efficient coordination, scheduling, and payment of service providers',
  },
  {
    value: 'Increase Resident Satisfaction',
    description: 'Improve resident experience through better services and communication',
  },
  {
    value: 'Modernize Operations',
    description: 'Transition from paper-based to digital processes and modern tools',
    tooltip: 'modernize-operations',
  },
  {
    value: 'Ensure Regulatory Compliance',
    description: 'Meet all legal requirements and maintain proper documentation',
    tooltip: 'regulatory-compliance-goal',
  },
  {
    value: 'Drive Cost Savings',
    description: 'Reduce expenses through group buying and operational efficiencies',
  },
  {
    value: 'Enable Growth/Scaling',
    description: 'Build systems that can handle community expansion or additional properties',
  },
  {
    value: 'Create Competitive Advantage',
    description: 'Stand out from other communities to attract and retain residents',
    tooltip: 'competitive-advantage',
  },
] as const;

export const STAKEHOLDER_TITLES = [
  'Board President',
  'Board Vice President',
  'Board Treasurer',
  'Board Secretary',
  'Board Member',
  'Property Manager',
  'Assistant Property Manager',
  'Community Manager',
  'Facilities Manager',
  'IT Contact',
  'Legal Counsel',
  'Accountant/CPA',
  'Maintenance Supervisor',
  'Security Manager',
  'Homeowner Representative',
  'Other',
] as const;

export const COMMUNITY_SIZE_OPTIONS = [
  { value: '50-100', label: '50-100 units' },
  { value: '101-200', label: '101-200 units' },
  { value: '201-400', label: '201-400 units' },
  { value: '401-800', label: '401-800 units' },
  { value: '800+', label: '800+ units' },
] as const;

export const COMMUNITY_TYPE_OPTIONS = [
  { value: 'hoa', label: 'Homeowners Association (HOA)' },
  { value: 'condo', label: 'Condominium Association' },
  { value: 'townhome', label: 'Townhome Community' },
  { value: 'gated', label: 'Gated Community' },
  { value: 'mixed', label: 'Mixed-Use Development' },
  { value: 'master', label: 'Master-Planned Community' },
  { value: 'other', label: 'Other' },
] as const;

export const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediately (within 30 days)' },
  { value: 'quarter', label: 'This quarter (within 90 days)' },
  { value: 'six-months', label: 'Within 6 months' },
  { value: 'annual', label: 'Within this fiscal year' },
  { value: 'exploring', label: 'Just exploring options' },
] as const;

export const BUDGET_RANGE_OPTIONS = [
  { value: 'under-25k', label: 'Under $25,000' },
  { value: '25k-50k', label: '$25,000 - $50,000' },
  { value: '50k-100k', label: '$50,000 - $100,000' },
  { value: '100k-200k', label: '$100,000 - $200,000' },
  { value: '200k-plus', label: '$200,000+' },
  { value: 'tbd', label: 'To be determined' },
] as const;

export const MEETING_TIME_OPTIONS = [
  { value: 'morning', label: 'Morning (9 AM - 12 PM)' },
  { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
  { value: 'evening', label: 'Evening (5 PM - 7 PM)' },
  { value: 'flexible', label: 'Flexible' },
] as const;
